package redmcrosco.alex.farmatch.models

import kotlinx.serialization.Serializable
import kotlinx.datetime.Instant

@Serializable
enum class AnnuncioTipo {
    OFFRO_LAVORO, // Usato dalle farmacie
    CERCO_LAVORO  // Usato dai farmacisti
}

@Serializable
enum class AnnuncioStato {
    APERTO, CHIUSO, CANCELLATO
}

@Serializable
enum class MatchStato {
    PROPOSTO, ACCETTATO, RIFIUTATO, CONFERMATO, COMPLETATO, ANNULLATO
}

@Serializable
enum class TariffaTipo {
    DIURNO, NOTTURNO, FESTIVO_DIURNO, FESTIVO_NOTTURNO
}

@Serializable
enum class CreatoreTipo {
    FARMACIA, FARMACISTA
}

@Serializable
data class Farmacista(
    val id: String? = null,
    val userId: String? = null,
    val nome: String,
    val cognome: String,
    val partitaIva: String,
    val codiceFiscale: String? = null,
    val numeroIscrizioneOrdine: String? = null,
    val sesso: String? = null,
    val dataNascita: String? = null,
    val luogoNascita: String? = null,
    val cittadinanza: String? = null,
    val titoloStudio: String? = null,
    val email: String,
    val password: String? = null,
    val telefono: String? = null,
    val regimeFiscale: String? = null,    // FORFETTARIO, ORDINARIO, ecc.
    val percEnpaf: Double = 2.0,          // Default 2%
    val regioneOperativita: String? = null,
    val provinciaOperativita: String? = null,
    val comuneOperativita: String? = null,
    val capOperativita: String? = null,
    val provinceOperativita: List<String> = emptyList(), // Lista di sigle province (es: ["MI", "BG"])
    val comuniOperativita: List<String> = emptyList(),  // Lista di nomi comuni (es: ["Milano", "Monza"])
    val disponibileTrasferta: Boolean = false,
    val lat: Double? = null,
    val lng: Double? = null,
    val descrizione: String? = null,
    val competenze: List<String> = emptyList(),
    val storicoCompletati: Int = 0,
    val storicoAnnullati: Int = 0,
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class Farmacia(
    val id: String? = null,
    val userId: String? = null,
    val nomeFarmacia: String,
    val ragioneSociale: String? = null, // Per fatturazione
    val partitaIva: String,
    val codiceFiscale: String? = null,
    val codiceSDI: String? = null,      // Per fatturazione
    val pec: String? = null,            // Per fatturazione
    val indirizzo: String,
    val cap: String? = null,
    val citta: String? = null,
    val provincia: String? = null,
    val regione: String? = null,
    val stato: String = "Italia",
    val lat: Double? = null,
    val lng: Double? = null,
    val email: String,
    val password: String? = null,
    val telefono: String,
    val numeroIscrizioneOrdineDirettore: String? = null,
    val tipo: String? = null,
    val descrizione: String? = null,
    val storicoCompletati: Int = 0,
    val storicoAnnullati: Int = 0,
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class AnnuncioTurno(
    val id: String? = null,
    val creatoDa: CreatoreTipo,
    val creatoreId: String,
    val tipo: AnnuncioTipo,
    val titolo: String,
    val descrizione: String? = null,
    val giornoSettimana: String? = null, // Aggiunto per gestire Lunedì, Martedì, ecc.
    val dataTurno: Instant,
    val dataTurnoFine: Instant? = null,
    val oraInizio: String,
    val oraFine: String,
    val zonaTestuale: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
    val compenso: Double? = null,
    val tariffaTipo: TariffaTipo = TariffaTipo.DIURNO,
    val stato: AnnuncioStato = AnnuncioStato.APERTO,
    val quantitaMassima: Int = 1,  // Numero di farmacisti richiesti
    val postiOccupati: Int = 0,    // Numero di farmacisti che hanno confermato
    val nomeCreatore: String? = null, // Aggiunto per visualizzazione rapida
    val localitaCreatore: String? = null, // Stile Immobiliare: via/città/cap
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class Match(
    val id: String? = null,
    val annuncioId: String,
    val farmacistaId: String,
    val farmaciaId: String,
    val stato: MatchStato = MatchStato.PROPOSTO,
    val messaggio: String? = null,
    val propostoDa: CreatoreTipo,
    val dataTurno: Instant? = null, // Aggiunto per ordinamento e grouping
    val nomeControparte: String? = null, // Aggiunto per visualizzare il nome nella Home
    val partitaIvaControparte: String? = null, // Aggiunto per visualizzare la P.IVA
    val regioneControparte: String? = null,    // Filtro geografico
    val provinciaControparte: String? = null,  // Filtro geografico
    val indirizzoControparte: String? = null,  // Per la UI stile immobiliare
    val cittaControparte: String? = null,     // Per la UI stile immobiliare
    val capControparte: String? = null,       // Per la UI stile immobiliare
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class UserReputation(
    val completedCount: Int = 0,
    val cancelledCount: Int = 0,
    val confirmedCount: Int = 0,
    val stars: Double = 0.0,
    val discountEligible: Boolean = false,
    val nextDiscountProgress: Int = 0, // Turni mancanti allo sconto
    val hasEnoughData: Boolean = false
)

@Serializable
data class Notification(
    val id: String? = null,
    val utenteId: String,
    val senderId: String? = null,
    val titolo: String,
    val messaggio: String,
    val tipo: String, // INFO, CONFIRM, CANCEL
    val data: Instant,
    val isRead: Boolean = false
)

@Serializable
data class Competenza(
    val id: String? = null,
    val nome: String,
    val descrizione: String? = null,
    val categoria: String? = null
)

@Serializable
data class FarmacistaCompetenza(
    val id: String? = null,
    val farmacistaId: String,
    val competenzaId: String,
    val livello: String? = null,
    val dataConseguimento: Instant? = null
)

@Serializable
data class BatchEntry(
    val dataTurno: Instant,
    val oraInizio: String,
    val oraFine: String,
    val compenso: Double? = null
)

@Serializable
data class BatchAnnuncioRequest(
    val template: AnnuncioTurno,
    val entries: List<BatchEntry>
)

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
    val role: UserRole? = null
)

@Serializable
data class LoginResponse(
    val success: Boolean,
    val role: UserRole? = null,
    val email: String? = null,
    val message: String? = null
)

@Serializable
data class PasswordResetRequest(val email: String)

@Serializable
data class PasswordResetConfirmRequest(val email: String, val code: String, val newPassword: String)

@Serializable
data class PasswordResetResponse(val success: Boolean, val message: String? = null)

@Serializable
data class StoricoEntry(
    val id: String? = null,
    val annuncioId: String,
    val farmacistaId: String,
    val farmaciaId: String,
    val dataTurno: Instant,
    val oraInizio: String,
    val oraFine: String,
    val compenso: Double,
    // Campi opzionali per la UI (popolati con join se serve)
    val nomeFarmacia: String? = null,
    val nomeFarmacista: String? = null
)

@Serializable
data class FatturaEntry(
    val id: String? = null,
    val numeroFattura: String,
    val annuncioIds: List<String>,
    val farmacistaId: String,
    val farmaciaId: String,
    val dataEmissione: Instant,
    val importoTotale: Double,
    val nomeFile: String,
    val nomeFarmacia: String? = null,
    val dataTurno: Instant? = null,
    val orario: String? = null
)

@Serializable
enum class AppThemeColor(val hex: String) {
    WHITE("0xFFFFFFFF"),
    GREEN("0xFF00A388"),
    BLUE("0xFF2196F3"),
    RED("0xFFE53935"),
    YELLOW("0xFFFFB300"),
    PINK("0xFFE91E63"),
    PURPLE("0xFF9C27B0"),
    ORANGE("0xFFFB8C00")
}

@Serializable
enum class SubscriptionType {
    FREE, TRIAL, MONTHLY, YEARLY
}

@Serializable
data class UserStatus(
    val userId: String,
    val isPremium: Boolean,
    val subscriptionType: SubscriptionType = SubscriptionType.FREE,
    val userRole: UserRole? = null,
    val subscribedAt: Long? = null,         // Data di sottoscrizione (Timestamp)
    val expiryDate: Long? = null,           // Data di scadenza (Timestamp)
    val daysRemaining: Int? = null
) {
    val isTrial: Boolean get() = subscriptionType == SubscriptionType.TRIAL
    
    // Controlla se l'abbonamento è ancora valido temporalmente
    val isValidPremium: Boolean get() {
        if (!isPremium) return false
        val expiry = expiryDate ?: return true // Se premium ma senza scadenza (es. admin), è valido
        return expiry > kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
    }
}
